import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Search, Menu, X, ChevronRight, Play, TrendingUp, Zap,
  Instagram, Twitter, Youtube, Calendar, ArrowUpRight
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { getArticles, getStats, getWriters, getF1Schedule } from '@/lib/api';

// Bormula Logo URL
const BORMULA_LOGO = "https://customer-assets.emergentagent.com/job_a6e857fc-9f8a-42a4-a234-830c8a3cc714/artifacts/98ozj9ol_Bormula%20Live%20Logo%201080x1350%20%281%29.png";

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Sample data for initial display
const SAMPLE_ARTICLES = [
  {
    id: '1',
    title: "CHARLES LECLERC'S PIANO ERA IS THE VIBE WE NEED",
    slug: 'verstappen-dominates-singapore',
    excerpt: "The Ferrari star isn't just fast on track. His latest single is climbing the charts and the paddock is obsessed.",
    category: 'news',
    image_url: 'https://images.unsplash.com/photo-1749952648908-d20b76c078dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxmb3JtdWxhJTIwMSUyMHJhY2UlMjBjYXIlMjBuaWdodCUyMHNwZWVkfGVufDB8fHx8MTc3MDc2NTY4N3ww&ixlib=rb-4.1.0&q=85',
    featured: true,
    trending: true,
    views: 45230,
    created_at: new Date().toISOString(),
    author: '@bormula_edit',
    timestamp: '2H AGO'
  },
  {
    id: '2',
    title: "SAUBER REBRAND: AUDI'S ARRIVAL CHANGES EVERYTHING",
    slug: 'hamilton-leclerc-battle',
    excerpt: "New colors, new energy. Here is why the 2026 shakeup is already starting to heat up the midfield.",
    category: 'news',
    image_url: 'https://images.unsplash.com/photo-1749952648879-fb2b68ab431e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwzfHxmb3JtdWxhJTIwMSUyMHJhY2UlMjBjYXIlMjBuaWdodCUyMHNwZWVkfGVufDB8fHx8MTc3MDc2NTY4N3ww&ixlib=rb-4.1.0&q=85',
    featured: false,
    trending: true,
    views: 32100,
    created_at: new Date().toISOString(),
    author: '@grid_talk',
    timestamp: '5H AGO'
  },
  {
    id: '3',
    title: "LAS VEGAS GP: THE NIGHT RACE THAT DEFINES MODERNITY",
    slug: 'mclaren-upgrades',
    excerpt: "Neon, speed, and sphere-sized memes. Las Vegas proves F1 is no longer just a sport—it is a spectacle.",
    category: 'news',
    image_url: 'https://images.unsplash.com/photo-1753480864732-4f496e49720a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHw0fHxmb3JtdWxhJTIwMSUyMHJhY2UlMjBjYXIlMjBuaWdodCUyMHNwZWVkfGVufDB8fHx8MTc3MDc2NTY4N3ww&ixlib=rb-4.1.0&q=85',
    featured: false,
    trending: false,
    views: 18450,
    created_at: new Date().toISOString(),
    author: '@bormula_admin',
    timestamp: '1D AGO'
  },
  {
    id: '5',
    title: 'RACE WEEKEND PREVIEW: ABU DHABI GP',
    slug: 'abu-dhabi-preview',
    excerpt: 'Everything you need to know about the season finale at Yas Marina Circuit.',
    category: 'race-updates',
    image_url: 'https://images.unsplash.com/photo-1761751237608-427f16f0a595?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxmb3JtdWxhJTIwMSUyMGRyaXZlciUyMGhlbG1ldCUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzcwNzY1NjkyfDA&ixlib=rb-4.1.0&q=85',
    featured: true,
    trending: false,
    views: 25600,
    created_at: new Date().toISOString()
  },
  {
    id: '6',
    title: 'BEST OVERTAKES OF THE MONTH',
    slug: 'best-overtakes',
    excerpt: 'Our picks for the most spectacular moves from the last four races.',
    category: 'reels',
    image_url: 'https://images.unsplash.com/photo-1605106702734-205df224ecce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxjaGVja2VyZWQlMjBmbGFnJTIwdGV4dHVyZSUyMGFic3RyYWN0fGVufDB8fHx8MTc3MDc2NTY5Nnww&ixlib=rb-4.1.0&q=85',
    featured: false,
    trending: true,
    views: 156000,
    created_at: new Date().toISOString()
  }
];

const STATS = [
  { platform: 'Instagram', value: '4.2M+', label: 'Engaged Community' },
  { platform: 'TikTok', value: '850M+', label: 'Total Video Views' },
  { platform: 'Newsletter', value: '250K+', label: 'Inner Circle' },
];

const UPCOMING_RACES = [];

// Navigation Component
const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <>
      <nav className="fixed top-0 left-0 w-full z-50 bg-black/80 backdrop-blur-xl border-b border-white/10 px-6 py-4" data-testid="main-navigation">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src={BORMULA_LOGO} alt="Bormula" className="h-10 w-10 object-contain" />
            <span className="font-display font-bold text-2xl tracking-tighter">BORMULA</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex gap-8 text-xs font-bold uppercase tracking-widest text-white/70">
            <a href="#news" className="hover:text-[#CCFF00] transition-colors" data-testid="nav-news">News</a>
            <Link to="/calendar" className="hover:text-[#CCFF00] transition-colors" data-testid="nav-calendar">Calendar</Link>
            <Link to="/education" className="hover:text-[#CCFF00] transition-colors" data-testid="nav-education">Education</Link>
            <Link to="/story" className="hover:text-[#CCFF00] transition-colors" data-testid="nav-story">Our Story</Link>
            <a href="#about" className="hover:text-[#CCFF00] transition-colors" data-testid="nav-about">About</a>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-6">
            {/* Social icons */}
            <div className="hidden sm:flex items-center gap-4 text-white/50">
              <Instagram size={18} className="hover:text-[#CCFF00] cursor-pointer transition-colors" />
              <Twitter size={18} className="hover:text-[#CCFF00] cursor-pointer transition-colors" />
              <Youtube size={18} className="hover:text-[#CCFF00] cursor-pointer transition-colors" />
            </div>

            {/* Search */}
            <form onSubmit={handleSearch} className="hidden lg:flex items-center">
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-40 bg-white/5 border-white/10 text-white placeholder:text-white/40 pr-10 rounded-xl"
                  data-testid="search-input"
                />
                <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-[#CCFF00]" data-testid="search-button">
                  <Search size={16} />
                </button>
              </div>
            </form>

            {/* Mobile menu button */}
            <button 
              className="md:hidden text-white"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {isMenuOpen ? <X /> : <Menu />}
            </button>

            {/* Join button */}
            <Link to="/calendar" className="hidden sm:block bg-[#CCFF00] text-black px-6 py-2 text-xs font-black uppercase tracking-widest rounded-full hover:scale-105 transition-transform">
              2026 Calendar
            </Link>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-[60] bg-black flex flex-col items-center justify-center gap-8 text-4xl font-display font-bold uppercase p-6">
          <button onClick={() => setIsMenuOpen(false)} className="absolute top-6 right-6 text-[#CCFF00]"><X size={40}/></button>
          <a href="#news" onClick={() => setIsMenuOpen(false)} className="hover:text-[#CCFF00] transition-colors">News</a>
          <Link to="/calendar" onClick={() => setIsMenuOpen(false)} className="hover:text-[#CCFF00] transition-colors">Calendar</Link>
          <Link to="/education" onClick={() => setIsMenuOpen(false)} className="hover:text-[#CCFF00] transition-colors">Education</Link>
          <Link to="/story" onClick={() => setIsMenuOpen(false)} className="hover:text-[#CCFF00] transition-colors">Our Story</Link>
          <a href="#about" onClick={() => setIsMenuOpen(false)} className="hover:text-[#CCFF00] transition-colors">About</a>
        </div>
      )}
    </>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex flex-col justify-center items-center overflow-hidden px-6" data-testid="hero-section">
      {/* Background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[#CCFF00]/10 via-black to-black -z-10"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-[600px] bg-[#CCFF00]/5 blur-[120px] rounded-full -z-10 animate-pulse-slow"></div>
      
      <div className="container mx-auto text-center max-w-5xl">
        <h2 className="text-[#CCFF00] font-bold tracking-[0.2em] md:tracking-[0.3em] uppercase mb-4 text-xs md:text-sm">Live from the grid</h2>
        <h1 className="text-4xl sm:text-6xl md:text-8xl lg:text-9xl font-display font-bold leading-[0.9] tracking-tighter mb-6 md:mb-8 uppercase" data-testid="hero-headline">
          THE SPEED <br /> 
          <span className="text-stroke">OF CULTURE</span>
        </h1>
        <p className="text-base md:text-2xl text-white/60 max-w-2xl mx-auto mb-8 md:mb-12 font-medium px-4">
          F1 media is broken. We fixed it. The fastest source for news, memes, and paddock drama. 
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center px-4">
          <a href="#news" className="w-full sm:w-auto">
            <button 
              className="w-full sm:w-auto bg-[#CCFF00] text-black px-8 md:px-12 py-4 md:py-5 text-base md:text-lg font-black uppercase tracking-wider rounded-xl hover:shadow-[0_0_40px_rgba(204,255,0,0.4)] transition-all btn-glow"
              data-testid="hero-cta-primary"
            >
              Latest Buzz
            </button>
          </a>
          <Link to="/calendar" className="w-full sm:w-auto">
            <button 
              className="w-full sm:w-auto border border-white/20 bg-white/5 backdrop-blur-md text-white px-8 md:px-12 py-4 md:py-5 text-base md:text-lg font-black uppercase tracking-wider rounded-xl hover:bg-white/10 transition-all flex items-center justify-center gap-2"
              data-testid="hero-cta-secondary"
            >
              <Calendar size={20} /> 2026 Calendar
            </button>
          </Link>
        </div>
      </div>

      {/* Scrolling Banner */}
      <div className="absolute bottom-0 w-full overflow-hidden whitespace-nowrap py-3 md:py-4 border-y border-white/10 bg-black/80 backdrop-blur-sm">
        <div className="flex animate-scroll-left text-lg md:text-2xl font-display font-bold uppercase tracking-tighter text-white/20">
          {Array(10).fill(null).map((_, i) => (
            <span key={i} className="mx-4 md:mx-8 flex items-center gap-2 md:gap-4">
              No Bullshit <Zap className="text-[#CCFF00] w-4 h-4 md:w-6 md:h-6" /> Next Gen Media <Zap className="text-[#CCFF00] w-4 h-4 md:w-6 md:h-6" /> High Contrast <Zap className="text-[#CCFF00] w-4 h-4 md:w-6 md:h-6" />
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

// Social Proof Section
const SocialProofSection = () => {
  return (
    <section className="py-24 border-b border-white/5 bg-[#0A0A0A] relative" data-testid="social-proof-section">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
          {STATS.map((stat, i) => (
            <div key={i} className="group cursor-default">
              <div className="text-[#CCFF00] font-display text-5xl md:text-7xl font-bold mb-2 group-hover:scale-110 transition-transform">
                {stat.value}
              </div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-white/40">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// News Section
const NewsSection = ({ articles, writers }) => {
  const newsArticles = articles.filter(a => a.category === 'news').slice(0, 3);

  const getWriterSlug = (article) => {
    if (!article.author_id) return null;
    const writer = writers.find(w => w.id === article.author_id);
    return writer?.slug || null;
  };

  return (
    <section id="news" className="py-24 px-6 container mx-auto" data-testid="news-section">
      <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-4">
        <div>
          <div className="flex items-center gap-2 text-[#CCFF00] mb-2 font-black uppercase tracking-widest text-sm">
            <Zap size={16} fill="#CCFF00" /> Fast Lane Headlines
          </div>
          <h3 className="text-5xl font-display font-bold uppercase tracking-tighter">Latest F1 News</h3>
        </div>
        <Link 
          to="/search?category=news"
          className="flex items-center gap-2 text-white/60 hover:text-[#CCFF00] font-bold uppercase text-xs tracking-widest transition-colors"
          data-testid="view-all-news"
        >
          View All Stories <ArrowUpRight size={16} />
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {newsArticles.map((item) => {
          const writerSlug = getWriterSlug(item);
          const authorDisplay = item.author_name || item.author || '@bormula';

          return (
            <div key={item.id} className="group cursor-pointer">
              <Link 
                to={`/article/${item.slug}`}
                data-testid={`article-card-${item.slug}`}
              >
                <div className="relative aspect-[4/3] overflow-hidden rounded-2xl mb-6">
                  <img 
                    src={item.image_url} 
                    alt={item.title} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute top-4 left-4 bg-[#CCFF00] text-black px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-sm">
                    {item.category}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60"></div>
                </div>
              </Link>
              <div className="flex items-center gap-3 text-white/40 text-[10px] font-black uppercase tracking-widest mb-3">
                {writerSlug ? (
                  <Link 
                    to={`/writer/${writerSlug}`} 
                    className="hover:text-[#CCFF00] transition-colors"
                    data-testid={`author-link-${item.slug}`}
                  >
                    {authorDisplay}
                  </Link>
                ) : (
                  <span>{authorDisplay}</span>
                )}
                <span className="w-1 h-1 bg-white/20 rounded-full"></span>
                <span>{formatDate(item.created_at)}</span>
              </div>
              <Link to={`/article/${item.slug}`}>
                <h4 className="text-2xl font-display font-bold leading-tight mb-4 group-hover:text-[#CCFF00] transition-colors uppercase">
                  {item.title}
                </h4>
                <p className="text-white/50 leading-relaxed font-medium line-clamp-2">
                  {item.excerpt}
                </p>
              </Link>
            </div>
          );
        })}
      </div>
    </section>
  );
};

// Upcoming Races Preview
const UpcomingRacesPreview = ({ upcomingRaces }) => {
  const races = upcomingRaces.length > 0 ? upcomingRaces : UPCOMING_RACES;

  const formatRaceDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase();
  };

  return (
    <section className="py-24 px-6 bg-[#0A0A0A]" data-testid="upcoming-races-section">
      <div className="container mx-auto">
        <div className="bg-[#111111] rounded-[40px] p-8 md:p-12 border border-white/5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#CCFF00]/10 blur-[100px] -z-10"></div>
          
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10">
            <div className="flex items-center gap-4">
              <Calendar className="text-[#CCFF00]" size={32} />
              <div>
                <h3 className="text-3xl font-display font-bold uppercase">Next Up</h3>
                <p className="text-white/40 text-sm">Upcoming Races</p>
              </div>
            </div>
            <Link 
              to="/calendar"
              className="flex items-center gap-2 bg-[#CCFF00] text-black px-6 py-3 rounded-full text-xs font-black uppercase tracking-widest hover:scale-105 transition-transform"
              data-testid="view-full-calendar"
            >
              Full Calendar & Standings <ChevronRight size={16} />
            </Link>
          </div>

          <div className="space-y-3">
            {races.map((race, i) => (
              <div 
                key={i} 
                className={`flex flex-col md:flex-row md:items-center justify-between p-5 rounded-2xl bg-black/50 border transition-colors group ${
                  i === 0 ? 'border-[#CCFF00]/30' : 'border-white/5 hover:border-[#CCFF00]/30'
                }`}
              >
                <div className="flex items-center gap-6 mb-3 md:mb-0">
                  <div className={`text-3xl font-display font-bold w-12 transition-colors ${
                    i === 0 ? 'text-[#CCFF00]' : 'text-white/20 group-hover:text-[#CCFF00]/30'
                  }`}>
                    {String(race.round).padStart(2, '0')}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-black uppercase tracking-widest text-[#CCFF00]">
                        {race.Circuit?.Location?.country || race.country || ''}
                      </span>
                      {i === 0 && (
                        <span className="px-2 py-0.5 bg-[#CCFF00] text-black text-[10px] font-black uppercase rounded animate-pulse">
                          Next
                        </span>
                      )}
                    </div>
                    <h4 className="text-xl font-display font-bold uppercase group-hover:text-[#CCFF00] transition-colors">
                      {race.raceName || race.circuit}
                    </h4>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <div className="text-[10px] font-black uppercase tracking-widest text-white/40">Race Day</div>
                    <div className="font-display font-bold">{race.date ? formatRaceDate(race.date) : race.date}</div>
                  </div>
                  <ChevronRight className="hidden md:block text-white/20 group-hover:text-[#CCFF00] transition-colors" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-24 px-6 bg-black" data-testid="about-section">
      <div className="container mx-auto max-w-4xl text-center">
        <h3 className="text-5xl md:text-6xl font-display font-bold uppercase mb-8 leading-[0.9] tracking-tighter">
          Bormula is the <span className="text-[#CCFF00]">Future</span> of F1 Media.
        </h3>
        <p className="text-white/50 text-xl leading-relaxed mb-12 max-w-2xl mx-auto">
          Born from a love for speed and a hatred for boring reporting, Bormula is a next-gen publisher for the internet-native fan. We don&apos;t just cover the race; we cover the culture.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-xl mx-auto">
          <div className="bg-[#111111] border border-white/5 p-8 rounded-2xl text-left">
            <div className="text-[#CCFF00] font-bold mb-2">Fastest Data</div>
            <p className="text-sm text-white/40">Real-time alerts that hit your phone before the checkered flag drops.</p>
          </div>
          <div className="bg-[#111111] border border-white/5 p-8 rounded-2xl text-left">
            <div className="text-[#CCFF00] font-bold mb-2">Cultural Edge</div>
            <p className="text-sm text-white/40">From driver fashion to the best paddock parties, we are there.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-12 border-t border-white/10 px-6" data-testid="footer">
      <div className="container mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
        <div className="flex items-center gap-2">
          <img src={BORMULA_LOGO} alt="Bormula" className="h-8 w-8 object-contain" />
          <span className="font-display font-bold text-xl tracking-tighter uppercase">Bormula</span>
        </div>
        <div className="flex gap-8 text-[10px] font-black uppercase tracking-widest text-white/40">
          <a href="#news" className="hover:text-white transition-colors">Archive</a>
          <Link to="/calendar" className="hover:text-white transition-colors">Calendar</Link>
          <Link to="/education" className="hover:text-white transition-colors">Education</Link>
          <Link to="/story" className="hover:text-white transition-colors">Our Story</Link>
        </div>
        <div className="flex gap-6 text-white/40">
          <Instagram size={20} className="hover:text-[#CCFF00] cursor-pointer transition-colors" />
          <Twitter size={20} className="hover:text-[#CCFF00] cursor-pointer transition-colors" />
          <Youtube size={20} className="hover:text-[#CCFF00] cursor-pointer transition-colors" />
        </div>
      </div>
      <div className="mt-12 text-center text-[10px] font-black uppercase tracking-[0.4em] text-white/10">
        &copy; 2024 BORMULA MEDIA GROUP. ALL GAS NO BRAKES.
      </div>
    </footer>
  );
};

// Main HomePage Component
export default function HomePage() {
  const [articles, setArticles] = useState(SAMPLE_ARTICLES);
  const [writers, setWriters] = useState([]);
  const [upcomingRaces, setUpcomingRaces] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [articlesData, statsData, writersData, scheduleData] = await Promise.all([
        getArticles({ limit: 20 }),
        getStats(),
        getWriters().catch(() => []),
        getF1Schedule().catch(() => ({ races: [] })),
      ]);
      
      if (articlesData.length > 0) {
        setArticles(articlesData);
      }
      setStats(statsData);
      setWriters(writersData);

      // Find next 3 upcoming races
      const today = new Date();
      const upcoming = (scheduleData.races || [])
        .filter(r => new Date(r.date + 'T00:00:00') >= today)
        .slice(0, 3);
      setUpcomingRaces(upcoming);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="min-h-screen font-sans">
      <Navigation />
      <HeroSection />
      <SocialProofSection />
      <NewsSection articles={articles} writers={writers} />
      <UpcomingRacesPreview upcomingRaces={upcomingRaces} />
      <AboutSection />
      <Footer />
    </div>
  );
}
